// Copyright 2020 TOKU Systems Inc

import React, { useState } from 'react';
import {
  Container,
  Button,
  FormControlLabel,
  CssBaseline,
  Avatar,
  Typography,
  TextField,
  Checkbox,
  Link,
  Icon,
  makeStyles,
  Backdrop,
  CircularProgress,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';
import * as _ from 'lodash';
import illumass from '../../services/illumass';
import { useDispatch, useSelector } from 'react-redux';
import { RarityAppState } from '../../store';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

export default function SignIn(): JSX.Element {
  const classes = useStyles();

  const [loggingIn, setLoggingIn] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const initialProps = useSelector<
    RarityAppState,
    { rememberMe: boolean; lastEmailTried: string }
  >((state) => {
    return {
      rememberMe: _.get(state, ['components', 'signIn', 'rememberMe'], false),
      lastEmailTried: _.get(
        state,
        ['components', 'signIn', 'lastEmailTried'],
        ''
      ),
    };
  });
  const dispatch = useDispatch();

  const [email, setEmail] = useState<string>(initialProps.lastEmailTried);
  const [password, setPassword] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(
    initialProps.rememberMe
  );
  const history = useHistory();
  const location = useLocation();
  const from = _.get(location, ['state', 'from'], { pathname: '/' });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    dispatch({ type: 'signInOptions', rememberMe, lastEmailTried: email });

    setLoggingIn(true);
    illumass.auth
      .login(email, password, process.env.REACT_APP_TOKEN_TTL)
      .then(() => {
        setLoggingIn(false);
        history.replace(from);
      })
      .catch((err) => {
        setLoggingIn(false);
        enqueueSnackbar(
          _.get(err, ['message'], 'Unexpected sign in failure.'),
          { variant: 'warning' }
        );
      });
  };

  return (
    <React.Fragment>
      <Backdrop open={loggingIn}>
        <CircularProgress />
      </Backdrop>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <Avatar className={classes.avatar}>
            <Icon>lock</Icon>
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          <form className={classes.form} noValidate onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event): void => setEmail(event.target.value)}
              disabled={loggingIn}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(event): void => setPassword(event.target.value)}
              disabled={loggingIn}
            />
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
              checked={rememberMe}
              onClick={(): void => setRememberMe(!rememberMe)}
              disabled={loggingIn}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={loggingIn}
            >
              Sign In
            </Button>
            <Link href="#" variant="body2">
              Forgot password?
            </Link>
          </form>
        </div>
      </Container>
    </React.Fragment>
  );
}
